import axios from 'axios';

const ENTRY_API = '/api/v2/tables/revenue-entry';

/** Вид временного правила: продление на сегодня, разблокировка таблицы, дата ввода */
export type EntryKind = 'extend' | 'unlock' | 'date';

export const ENTRY_KINDS: EntryKind[] = ['extend', 'unlock', 'date'];

/** Окно сети: ввод за день открывается в open этого дня и закрывается в close следующего ('HH:MM') */
export interface EntrySchedule {
  open: string;
  close: string;
}

/** Действующее правило; моменты - время Алматы строками 'YYYY-MM-DD HH:MM', даты - 'YYYY-MM-DD' */
export interface EntryRule {
  id: number;
  date: string | null;
  until: string;
}

export type EntryRules = Record<EntryKind, EntryRule | null>;

/** Текущее состояние ввода для сети: открыт до until или закрыт до opensAt */
export interface EntryState {
  open: boolean;
  date: string;
  until?: string;
  opensAt?: string;
  source: string;
}

/** Границы и значения по умолчанию для форм, рассчитанные сервером на момент now */
export interface EntryBounds {
  now: string;
  maxUntil: string;
  minDate: string;
  maxDate: string;
  /** Дата, которую продлевает продление для сети */
  extendDate: string;
  /** Продление должно заканчиваться позже этого момента (обычное закрытие) */
  extendAfter: string;
  defaults: Record<EntryKind, string>;
}

/** Исключение аптеки зоны оператора */
export interface EntryOverride {
  id: number;
  pharmacy: string;
  pharmacyNum: string;
  fullName: string | null;
  kind: EntryKind;
  date: string | null;
  until: string;
}

export interface EntryPharmacy {
  username: string;
  pharmacyNum: string;
  fullName: string | null;
}

export interface EntryData {
  schedule: EntrySchedule;
  network: EntryRules;
  state: EntryState;
  bounds: EntryBounds;
  overrides: EntryOverride[];
  /** Аптеки зоны для выбора в исключениях; null - у оператора нет права задавать исключения */
  pharmacies: EntryPharmacy[] | null;
}

/** Новое правило: срок обязателен, дата - только для вида date */
export interface EntryRuleInput {
  until: string;
  date?: string;
}

interface DataResponse {
  success?: boolean;
  data?: Partial<EntryData>;
}

interface MessageResponse {
  success?: boolean;
  message?: string;
  cleared?: boolean;
}

function messageOf(res: { data?: MessageResponse }, fallback: string): string {
  return (res.data && res.data.message) || fallback;
}

export async function fetchEntry(): Promise<EntryData> {
  const res = await axios.get<DataResponse>(ENTRY_API);
  const data = res.data && res.data.success ? res.data.data : undefined;
  if (
    !data || !data.schedule || !data.network || !data.state || !data.bounds || !data.bounds.defaults ||
    !Array.isArray(data.overrides) || (data.pharmacies !== null && !Array.isArray(data.pharmacies))
  ) {
    throw new Error('Сервер вернул окно ввода в неожиданном виде');
  }
  return data as EntryData;
}

export async function saveSchedule(schedule: EntrySchedule): Promise<string> {
  const res = await axios.put<MessageResponse>(`${ENTRY_API}/schedule`, schedule);
  return messageOf(res, 'Окно ввода сохранено');
}

/** Правило для всей сети; дату ввода сервер принимает только вместе с подтверждением confirm (та же дата) */
export async function setNetworkRule(kind: EntryKind, input: EntryRuleInput, confirm?: string): Promise<string> {
  const res = await axios.put<MessageResponse>(`${ENTRY_API}/network/${kind}`, { ...input, confirm });
  return messageOf(res, 'Параметр сохранен');
}

/** Отмена правила сети; cleared = false - правило уже не действовало (его срок прошел) */
export async function clearNetworkRule(kind: EntryKind): Promise<{ cleared: boolean; message: string }> {
  const res = await axios.delete<MessageResponse>(`${ENTRY_API}/network/${kind}`);
  return { cleared: !(res.data && res.data.cleared === false), message: messageOf(res, 'Параметр отменен') };
}

/** Одно правило для одной или нескольких аптек (логины users.username) */
export async function addOverrides(pharmacies: string[], kind: EntryKind, input: EntryRuleInput): Promise<string> {
  const res = await axios.post<MessageResponse>(`${ENTRY_API}/overrides`, { pharmacies, kind, ...input });
  return messageOf(res, 'Исключения сохранены');
}

export async function deleteOverride(id: number): Promise<string> {
  const res = await axios.delete<MessageResponse>(`${ENTRY_API}/overrides/${id}`);
  return messageOf(res, 'Исключение отменено');
}
